import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  Container,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Grid,
} from "@mui/material";
import { pageHeading } from "../styles/styling";
import { format } from "date-fns";
import LoadingOverlay from "../components/LoadingOverlay";
import {
  listBatchNotifications,
  viewBatchNotification,
} from "../redux/slices/notificationsSlice";

const NotificationFileList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const idToken = localStorage.getItem("idToken");

  const { batchNotifications, hasMoreBatches, status, error } = useSelector(
    (state: RootState) => state.notifications
  );

  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const rowsPerPageArr = [10, 15, 25, 50, 75, 100];

  const columns = [
    { field: "fileName", headerName: "Filename", width: "120px" },
    { field: "createdOn", headerName: "Upload Date", width: "120px" },
    { field: "createdBy", headerName: "Uploaded By", width: "90px" },
    {
      field: "status",
      headerName: "Status",
      width: "100px",
    },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }

    dispatch(
      listBatchNotifications({ limit: rowsPerPage, cursor: currentPage })
    );
  }, [currentPage, dispatch, idToken, navigate, rowsPerPage]);

  if (error) {
    return (
      <Grid
        item
        xs={12}
        md={9}
        sx={{
          height: "90vh",
          overflowY: "auto",
          padding: 2,
        }}
      >
        <Container sx={{ marginLeft: "240px", flexGrow: 1, padding: 2 }}>
          <Typography variant="h6" color="error">
            Error: {typeof error === "string" ? error : error?.message}
          </Typography>
        </Container>
      </Grid>
    );
  }

  return (
    <Grid
      item
      xs={12}
      md={9}
      sx={{
        height: "90vh",
        overflowY: "auto",
        padding: 2,
      }}
    >
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="h4" sx={pageHeading}>
          Notification File List
        </Typography>
      </Box>

      <LoadingOverlay loading={status === "loading"} />

      {batchNotifications.length === 0 ? (
        <Typography>No available records.</Typography>
      ) : (
        <Paper sx={{ width: "100%" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      align={"left"}
                      style={{ width: column.width, fontWeight: "bold" }}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {batchNotifications.map((row: any) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      onClick={async () => {
                        await dispatch(viewBatchNotification(row.id)).then(
                          () => {
                            navigate("/notification-view-batch");
                          }
                        );
                      }}
                    >
                      {columns.map((column: any) => {
                        const value = row[column.field];
                        return (
                          <TableCell
                            key={`${row.id} - ${column.field}`}
                            align={"left"}
                          >
                            {column.field === "createdOn"
                              ? format(value, "MM/dd/yyyy kk:mm:ss")
                              : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={rowsPerPageArr}
            component="div"
            count={
              hasMoreBatches
                ? (currentPage + 1) * rowsPerPage + 1
                : currentPage * rowsPerPage
            }
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ page }) => `Showing page ${page + 1}`}
          />
        </Paper>
      )}
    </Grid>
  );
};

export default NotificationFileList;
